/* eslint-disable no-console */
import {
  PayPalScriptProvider,
  ReactPayPalScriptOptions,
} from '@paypal/react-paypal-js';
import React, { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import CustomerInfo from '../../../types/CustomerInfo';
import Invoice from '../../../types/Invoice';
import WebCheckoutCaptureResponsePayPalType from '../../../types/response/WebCheckoutCaptureResponsePayPalType';
import PayPal from './PayPal';

type PropType = {
  customerInfo: CustomerInfo;
  invoice: Invoice;
  invoiceId: string;
  deliveryMethod: string;
  onPaymentSubmit: (
    paymentToken: string,
    paypalresponse?: WebCheckoutCaptureResponsePayPalType
  ) => Promise<void>;
};

const PayPalWrapper = ({
  customerInfo,
  invoice,
  invoiceId,
  deliveryMethod,
  onPaymentSubmit,
}: PropType): JSX.Element => {
  const fundingOptions =
    (invoice.invoiceDetails?.order?.orderTotal ?? 0) < 30
      ? 'card,paylater'
      : 'card';

  const initialOptions: ReactPayPalScriptOptions = useMemo(() => {
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    
    if (!clientId) {
      const errorMessage = 'PayPal clientId is missing';
      console.error('[PayPalWrapper]', errorMessage);
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('component', 'PayPalWrapper');
        scope.setTag('action', 'configuration');
        scope.setTag('invoiceId', invoiceId);
        Sentry.captureMessage(errorMessage);
      });
    }

    if (!invoice.merchantId) {
      const errorMessage = 'PayPal merchantId is missing from invoice';
      console.error('[PayPalWrapper]', errorMessage);
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('component', 'PayPalWrapper');
        scope.setTag('action', 'configuration');
        scope.setTag('invoiceId', invoiceId);
        Sentry.captureMessage(errorMessage);
      });
    }

    return {
      clientId: clientId as string,
      merchantId: invoice.merchantId,
      dataPartnerAttributionId: process.env.REACT_APP_PAYPAL_BN_CODE,
      disableFunding: fundingOptions,
      intent: 'authorize',
    };
  }, [invoice.merchantId, fundingOptions, invoiceId]);

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PayPal
        onPaymentSubmit={onPaymentSubmit}
        customerInfo={customerInfo}
        invoice={invoice}
        invoiceId={invoiceId}
        deliveryMethod={deliveryMethod}
      />
    </PayPalScriptProvider>
  );
};

export default PayPalWrapper;
